import React, { useState, useEffect } from 'react';
import {
  Save,
  Loader2,
  CheckCircle,
  XCircle,
  Mail,
  Plus,
  Tag,
  Eye,
  EyeOff,
  Wifi,
  Key,
  Download,
  FolderOpen,
  RefreshCw,
  ScrollText,
  User,
  Lock,
} from 'lucide-react';
import {
  getImapConfig,
  updateImapConfig,
  testImapConnection,
  triggerImapPoll,
  getImapFolders,
  getCategorias,
  createCategoria,
  getKeysConfig,
  updateKeysConfig,
  getImapLog,
  clearImapLog,
  getAdminConfig,
  updateAdminConfig,
  logout,
  login,
} from '../services/api';
import toast from 'react-hot-toast';

export default function Settings() {
  const [imapForm, setImapForm] = useState({
    host: '',
    port: 993,
    username: '',
    password: '',
    folder: 'INBOX',
    interval_minutes: 30,
    enabled: false,
  });
  const [imapLoading, setImapLoading] = useState(true);
  const [imapSaving, setImapSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [polling, setPolling] = useState(false);
  const [imapFolders, setImapFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [showFolderPicker, setShowFolderPicker] = useState(false);

  const [categorias, setCategorias] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [newCategoria, setNewCategoria] = useState('');
  const [catSaving, setCatSaving] = useState(false);

  const [llmKey, setLlmKey] = useState('');
  const [showLlmKey, setShowLlmKey] = useState(false);
  const [deepreadKey, setDeepreadKey] = useState('');
  const [showDeepreadKey, setShowDeepreadKey] = useState(false);
  const [secretKey, setSecretKey] = useState('');
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [keysLoading, setKeysLoading] = useState(true);
  const [keysSaving, setKeysSaving] = useState(false);
  const [keysInfo, setKeysInfo] = useState({ llm_api_key_set: false, deepread_api_key_set: false, secret_key_set: false });

  const [imapLog, setImapLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logVisible, setLogVisible] = useState(false);

  const [adminUser, setAdminUser] = useState('');
  const [adminPass, setAdminPass] = useState('');
  const [adminPassConfirm, setAdminPassConfirm] = useState('');
  const [showAdminPass, setShowAdminPass] = useState(false);
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    getKeysConfig()
      .then((data) => {
        if (cancelled) return;
        setKeysInfo(data);
        setLlmKey(data.llm_api_key || '');
        setDeepreadKey(data.deepread_api_key || '');
        setSecretKey(data.secret_key || '');
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setKeysLoading(false); });

    getImapConfig()
      .then((data) => {
        if (cancelled) return;
        if (data) {
          setImapForm({
            host: data.imap_host || '',
            port: data.imap_port ?? 993,
            username: data.imap_user || '',
            password: data.imap_password || '',
            folder: data.imap_folder || 'INBOX',
            interval_minutes: data.imap_poll_interval ?? 30,
            enabled: data.imap_enabled ?? false,
          });
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setImapLoading(false); });

    getCategorias()
      .then((data) => { if (!cancelled) setCategorias(Array.isArray(data) ? data : []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setCatLoading(false); });

    getAdminConfig()
      .then((data) => { if (!cancelled) setAdminUser(data.admin_user || ''); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setAdminLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const handleImapSave = async () => {
    setImapSaving(true);
    try {
      // Map frontend field names to backend field names
      const payload = {
        imap_host: imapForm.host,
        imap_port: imapForm.port,
        imap_user: imapForm.username,
        imap_password: imapForm.password,
        imap_folder: imapForm.folder,
        imap_poll_interval: imapForm.interval_minutes,
        imap_enabled: imapForm.enabled,
      };
      // Don't send masked password back to the server
      if (payload.imap_password === '****' || payload.imap_password === '') {
        delete payload.imap_password;
      }
      await updateImapConfig(payload);
      // Re-fetch config from backend to keep form state in sync
      const fresh = await getImapConfig();
      if (fresh) {
        setImapForm({
          host: fresh.imap_host || '',
          port: fresh.imap_port ?? 993,
          username: fresh.imap_user || '',
          password: fresh.imap_password || '',
          folder: fresh.imap_folder || 'INBOX',
          interval_minutes: fresh.imap_poll_interval ?? 30,
          enabled: fresh.imap_enabled ?? false,
        });
      }
      toast.success('Configuración IMAP guardada');
    } catch (err) {
      toast.error('Error guardando: ' + err.message);
    } finally {
      setImapSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testImapConnection();
      setTestResult({ success: true, message: result.message || 'Conexión exitosa' });
      toast.success('Conexión IMAP exitosa');
    } catch (err) {
      setTestResult({ success: false, message: err.message || 'Error de conexión' });
      toast.error('Error de conexión: ' + err.message);
    } finally {
      setTesting(false);
    }
  };

  const handleAddCategoria = async (e) => {
    e.preventDefault();
    if (!newCategoria.trim()) return;
    setCatSaving(true);
    try {
      await createCategoria({ nombre: newCategoria.trim() });
      toast.success('Categoría creada');
      setNewCategoria('');
      const data = await getCategorias();
      setCategorias(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Error creando categoría: ' + err.message);
    } finally {
      setCatSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="text-2xl font-bold text-gray-900">Configuración</h1>

      {/* Admin Credentials */}
      <div className="card">
        <div className="flex items-center gap-2 mb-6">
          <User className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Credenciales de Acceso</h2>
        </div>

        {adminLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Usuario
              </label>
              <input
                type="text"
                value={adminUser}
                onChange={(e) => setAdminUser(e.target.value)}
                className="input-field text-sm"
                placeholder="admin"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Nueva contraseña
              </label>
              <div className="relative">
                <input
                  type={showAdminPass ? 'text' : 'password'}
                  value={adminPass}
                  onChange={(e) => setAdminPass(e.target.value)}
                  className="input-field text-sm pr-10"
                  placeholder="Dejar vacío para no cambiar"
                />
                <button
                  type="button"
                  onClick={() => setShowAdminPass(!showAdminPass)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showAdminPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Confirmar contraseña
              </label>
              <input
                type={showAdminPass ? 'text' : 'password'}
                value={adminPassConfirm}
                onChange={(e) => setAdminPassConfirm(e.target.value)}
                className="input-field text-sm"
                placeholder="Repetir contraseña"
              />
            </div>
            <div className="flex justify-end pt-2">
              <button
                onClick={async () => {
                  if (adminPass && adminPass !== adminPassConfirm) {
                    toast.error('Las contraseñas no coinciden');
                    return;
                  }
                  setAdminSaving(true);
                  try {
                    const payload = {};
                    if (adminUser.trim()) payload.admin_user = adminUser.trim();
                    if (adminPass) payload.admin_password = adminPass;
                    if (Object.keys(payload).length === 0) {
                      toast.error('No hay cambios que guardar');
                      return;
                    }
                    await updateAdminConfig(payload);
                    toast.success('Credenciales actualizadas. Vuelve a iniciar sesión.');
                    // Re-login with new credentials
                    const newUser = payload.admin_user || adminUser;
                    const newPass = payload.admin_password;
                    if (newPass) {
                      logout();
                      try {
                        await login(newUser, newPass);
                        setAdminPass('');
                        setAdminPassConfirm('');
                      } catch {
                        window.location.reload();
                      }
                    }
                  } catch (err) {
                    toast.error('Error guardando: ' + err.message);
                  } finally {
                    setAdminSaving(false);
                  }
                }}
                disabled={adminSaving}
                className="btn-primary flex items-center gap-2 text-sm"
              >
                {adminSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Lock className="w-4 h-4" />
                )}
                Guardar Credenciales
              </button>
            </div>
          </div>
        )}
      </div>

      {/* IMAP Config */}
      <div className="card">
        <div className="flex items-center gap-2 mb-6">
          <Mail className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Configuración IMAP</h2>
        </div>

        {imapLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Servidor IMAP
                </label>
                <input
                  type="text"
                  value={imapForm.host}
                  onChange={(e) => setImapForm({ ...imapForm, host: e.target.value })}
                  className="input-field text-sm"
                  placeholder="imap.gmail.com"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Puerto</label>
                <input
                  type="number"
                  value={imapForm.port}
                  onChange={(e) =>
                    setImapForm({ ...imapForm, port: parseInt(e.target.value) || 993 })
                  }
                  className="input-field text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Usuario</label>
                <input
                  type="text"
                  value={imapForm.username}
                  onChange={(e) =>
                    setImapForm({ ...imapForm, username: e.target.value })
                  }
                  className="input-field text-sm"
                  placeholder="usuario@ejemplo.com"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Contrasena</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={imapForm.password}
                    onChange={(e) =>
                      setImapForm({ ...imapForm, password: e.target.value })
                    }
                    className="input-field text-sm pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Carpeta</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={imapForm.folder}
                    onChange={(e) => setImapForm({ ...imapForm, folder: e.target.value })}
                    className="input-field text-sm flex-1"
                    placeholder="INBOX"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      setLoadingFolders(true);
                      try {
                        const result = await getImapFolders();
                        if (result.success && result.folders?.length > 0) {
                          setImapFolders(result.folders);
                          setShowFolderPicker(true);
                        } else {
                          toast.error(result.error || 'No se encontraron carpetas. Guarda primero la configuracion IMAP.');
                        }
                      } catch (err) {
                        toast.error('Error listando carpetas: ' + err.message);
                      } finally {
                        setLoadingFolders(false);
                      }
                    }}
                    disabled={loadingFolders}
                    className="btn-secondary flex items-center gap-1 text-sm px-3 shrink-0"
                    title="Explorar carpetas IMAP"
                  >
                    {loadingFolders ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <FolderOpen className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {showFolderPicker && imapFolders.length > 0 && (
                  <div className="mt-2 border border-gray-200 rounded-lg bg-white shadow-sm max-h-48 overflow-y-auto">
                    {imapFolders.map((folder) => (
                      <button
                        key={folder}
                        type="button"
                        onClick={() => {
                          setImapForm({ ...imapForm, folder });
                          setShowFolderPicker(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 transition-colors flex items-center gap-2 ${
                          imapForm.folder === folder ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'
                        }`}
                      >
                        <FolderOpen className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        {folder}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Intervalo (minutos)
                </label>
                <input
                  type="number"
                  min={1}
                  value={imapForm.interval_minutes}
                  onChange={(e) =>
                    setImapForm({
                      ...imapForm,
                      interval_minutes: parseInt(e.target.value) || 30,
                    })
                  }
                  className="input-field text-sm"
                />
              </div>
            </div>

            {/* Enabled toggle */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setImapForm({ ...imapForm, enabled: !imapForm.enabled })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  imapForm.enabled ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    imapForm.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-700">
                {imapForm.enabled ? 'Activado' : 'Desactivado'}
              </span>
            </div>

            {/* Test result */}
            {testResult && (
              <div
                className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                  testResult.success
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}
              >
                {testResult.success ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                {testResult.message}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleTestConnection}
                disabled={testing}
                className="btn-secondary flex items-center gap-2 text-sm"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Wifi className="w-4 h-4" />
                )}
                Probar Conexión
              </button>
              <button
                onClick={async () => {
                  setPolling(true);
                  try {
                    const result = await triggerImapPoll();
                    if (result.status === 'disabled') {
                      toast.error('IMAP está desactivado. Actívalo y guarda primero.');
                    } else if (result.processed === 0 && !result.pending_downloads) {
                      toast('No se encontraron nuevos archivos en el buzón');
                    } else {
                      let msg = '';
                      if (result.processed > 0) {
                        msg += `Importadas ${result.successful || 0} de ${result.processed} facturas. `;
                      }
                      if (result.pending_downloads > 0) {
                        msg += `${result.pending_downloads} email(s) con enlaces de descarga (ver Panel de Control).`;
                      }
                      if (msg) {
                        toast.success(msg.trim());
                      } else {
                        toast('No se encontraron nuevos archivos en el buzón');
                      }
                    }
                  } catch (err) {
                    toast.error('Error importando: ' + err.message);
                  } finally {
                    setPolling(false);
                  }
                }}
                disabled={polling}
                className="btn-secondary flex items-center gap-2 text-sm"
              >
                {polling ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Importar Ahora
              </button>
              <button
                onClick={async () => {
                  setPolling(true);
                  try {
                    const result = await triggerImapPoll({ searchAll: true });
                    if (result.status === 'disabled') {
                      toast.error('IMAP está desactivado. Actívalo y guarda primero.');
                    } else if (result.processed === 0 && !result.pending_downloads) {
                      toast('No se encontraron archivos en el buzón');
                    } else {
                      let msg = '';
                      if (result.processed > 0) {
                        msg += `Importadas ${result.successful || 0} de ${result.processed} facturas. `;
                      }
                      if (result.pending_downloads > 0) {
                        msg += `${result.pending_downloads} email(s) con enlaces de descarga.`;
                      }
                      if (msg) {
                        toast.success(msg.trim());
                      } else {
                        toast('No se encontraron archivos en el buzón');
                      }
                    }
                  } catch (err) {
                    toast.error('Error importando: ' + err.message);
                  } finally {
                    setPolling(false);
                  }
                }}
                disabled={polling}
                className="btn-secondary flex items-center gap-2 text-sm"
                title="Importar todos los emails de la carpeta, incluidos los ya leídos"
              >
                {polling ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Importar Todos
              </button>
              <button
                onClick={handleImapSave}
                disabled={imapSaving}
                className="btn-primary flex items-center gap-2 text-sm"
              >
                {imapSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Guardar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* IMAP Activity Log */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ScrollText className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Log de Actividad IMAP</h2>
          </div>
          <div className="flex gap-2">
            <button
              onClick={async () => {
                setLogVisible(true);
                setLogLoading(true);
                try {
                  const data = await getImapLog(100);
                  setImapLog(Array.isArray(data) ? data : []);
                } catch (err) {
                  toast.error('Error cargando log: ' + err.message);
                } finally {
                  setLogLoading(false);
                }
              }}
              className="btn-secondary text-sm flex items-center gap-1"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Cargar
            </button>
            {imapLog.length > 0 && (
              <button
                onClick={async () => {
                  try {
                    await clearImapLog();
                    setImapLog([]);
                    toast.success('Log limpiado');
                  } catch (err) {
                    toast.error('Error: ' + err.message);
                  }
                }}
                className="btn-secondary text-sm text-red-600 hover:text-red-700"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>

        {logVisible && (
          logLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            </div>
          ) : imapLog.length > 0 ? (
            <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Fecha</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Nivel</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Mensaje</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {imapLog.map((entry) => (
                    <tr key={entry.id} className={entry.nivel === 'error' ? 'bg-red-50' : ''}>
                      <td className="px-3 py-1.5 text-xs text-gray-500 whitespace-nowrap">
                        {entry.timestamp ? new Date(entry.timestamp).toLocaleString('es-ES') : '-'}
                      </td>
                      <td className="px-3 py-1.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          entry.nivel === 'error' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {entry.nivel}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-xs text-gray-700">{entry.mensaje}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4">No hay entradas en el log</p>
          )
        )}
      </div>

      {/* Categories */}
      <div className="card">
        <div className="flex items-center gap-2 mb-6">
          <Tag className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Categorías</h2>
        </div>

        <form onSubmit={handleAddCategoria} className="flex gap-2 mb-4">
          <input
            type="text"
            value={newCategoria}
            onChange={(e) => setNewCategoria(e.target.value)}
            placeholder="Nueva categoría..."
            className="input-field text-sm flex-1"
          />
          <button
            type="submit"
            disabled={catSaving || !newCategoria.trim()}
            className="btn-primary flex items-center gap-1 text-sm"
          >
            {catSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Añadir
          </button>
        </form>

        {catLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
          </div>
        ) : categorias.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {categorias.map((cat) => (
              <span
                key={cat.id || cat.nombre}
                className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm"
              >
                <Tag className="w-3 h-3 mr-1.5 text-gray-400" />
                {cat.nombre}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400">No hay categorías definidas</p>
        )}
      </div>

      {/* API Keys */}
      <div className="card">
        <div className="flex items-center gap-2 mb-6">
          <Key className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Claves de Seguridad</h2>
        </div>

        {keysLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                API Key Anthropic (Claude)
              </label>
              <div className="relative">
                <input
                  type={showLlmKey ? 'text' : 'password'}
                  value={llmKey}
                  onChange={(e) => setLlmKey(e.target.value)}
                  className="input-field text-sm pr-10"
                  placeholder="sk-ant-..."
                />
                <button
                  type="button"
                  onClick={() => setShowLlmKey(!showLlmKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showLlmKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Fallback para OCR si DeepRead no esta configurado. {keysInfo.llm_api_key_set ? '(configurada)' : '(no configurada)'}
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                API Key DeepRead (OCR principal)
              </label>
              <div className="relative">
                <input
                  type={showDeepreadKey ? 'text' : 'password'}
                  value={deepreadKey}
                  onChange={(e) => setDeepreadKey(e.target.value)}
                  className="input-field text-sm pr-10"
                  placeholder="dr-..."
                />
                <button
                  type="button"
                  onClick={() => setShowDeepreadKey(!showDeepreadKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showDeepreadKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Proveedor OCR principal para extraccion de facturas. {keysInfo.deepread_api_key_set ? '(configurada)' : '(no configurada)'}
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Secret Key (Encriptacion)
              </label>
              <div className="relative">
                <input
                  type={showSecretKey ? 'text' : 'password'}
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  className="input-field text-sm pr-10"
                  placeholder="clave-secreta-aleatoria"
                />
                <button
                  type="button"
                  onClick={() => setShowSecretKey(!showSecretKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Se usa para encriptar datos sensibles. {keysInfo.secret_key_set ? '(configurada)' : '(no configurada)'}
              </p>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={async () => {
                  setKeysSaving(true);
                  try {
                    const payload = {};
                    if (llmKey && !llmKey.includes('****')) payload.llm_api_key = llmKey;
                    if (deepreadKey && !deepreadKey.includes('****')) payload.deepread_api_key = deepreadKey;
                    if (secretKey && !secretKey.includes('****')) payload.secret_key = secretKey;
                    if (Object.keys(payload).length === 0) {
                      toast.error('Introduce valores nuevos (no enmascarados)');
                      return;
                    }
                    await updateKeysConfig(payload);
                    toast.success('Claves guardadas correctamente');
                    const fresh = await getKeysConfig();
                    setKeysInfo(fresh);
                    setLlmKey(fresh.llm_api_key || '');
                    setDeepreadKey(fresh.deepread_api_key || '');
                    setSecretKey(fresh.secret_key || '');
                  } catch (err) {
                    toast.error('Error guardando: ' + err.message);
                  } finally {
                    setKeysSaving(false);
                  }
                }}
                disabled={keysSaving}
                className="btn-primary flex items-center gap-2 text-sm"
              >
                {keysSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Guardar Claves
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
